/**
 * One person, one colour, everywhere on the screen.
 *
 * The transcript, the activity ribbon, the talk-share bars and the speaker rail
 * are four views of the same two or three people. If a person is teal in one of
 * them and orange in another, the reader has to re-learn who is who at every
 * glance, and the page stops being a single picture of a conversation.
 *
 * So the mapping is built ONCE, from the session's participant order, and every
 * component reads it. The colour comes from the person's POSITION in that
 * roster, never from a hash of the identity: a hash would let two people in the
 * same call collide, which is the only collision that actually matters here.
 * (`speakerColorVar` in `lib/format.ts` is the same rule, and this is the thing
 * that decides the index it is given.)
 *
 * Identities that appear in the transcript, the activity document or the
 * metrics but NOT in `participants[]` are appended in the order they were
 * discovered. Appending is deliberate: it cannot renumber anybody who was
 * already on screen, so a colour never changes under the reader when the
 * transcript finishes loading a second after the header did.
 */

import { speakerColorVar } from '@/lib/format'
import type { SessionParticipantView } from '@era/shared'

export interface Speaker {
  identity: string
  /**
   * The human name, from this app's SQLite. Falls back to the identity for a
   * session this app did not create — upstream never stored a name, so
   * `u_a` is genuinely all there is. See FINDINGS.md #10.
   */
  displayName: string
  /** Position in the roster. The only input to the colour. */
  index: number
  /** `var(--speaker-N)`, resolved by the browser, so it flips with the theme. */
  color: string
  isCreator: boolean
}

export interface SpeakerRoster {
  list: Speaker[]
  /**
   * Never returns undefined. An identity that turns up in a document but not in
   * the roster still has to render — silently dropping a turn because its
   * speaker was not in `participants[]` would lose transcript content, which is
   * a far worse failure than an extra colour.
   */
  get(identity: string): Speaker
}

export function buildRoster(
  participants: SessionParticipantView[],
  extras: Iterable<string> = [],
): SpeakerRoster {
  const list: Speaker[] = []
  const byIdentity = new Map<string, Speaker>()

  const add = (identity: string, displayName: string, isCreator: boolean) => {
    if (byIdentity.has(identity)) return
    const speaker: Speaker = {
      identity,
      displayName: displayName || identity,
      index: list.length,
      color: speakerColorVar(list.length),
      isCreator,
    }
    list.push(speaker)
    byIdentity.set(identity, speaker)
  }

  for (const participant of participants) {
    add(participant.identity, participant.display_name, participant.is_creator)
  }
  for (const identity of extras) {
    add(identity, identity, false)
  }

  return {
    list,
    get(identity) {
      const known = byIdentity.get(identity)
      if (known) return known
      // A late discovery — a turn whose speaker was in neither list. Given a
      // colour from beyond the roster rather than dropped.
      const fallback: Speaker = {
        identity,
        displayName: identity,
        index: list.length,
        color: speakerColorVar(list.length),
        isCreator: false,
      }
      list.push(fallback)
      byIdentity.set(identity, fallback)
      return fallback
    },
  }
}

/** `Ana Ruiz` → `Ana`. Long names wreck a rail that is 240px wide. */
export function firstName(displayName: string): string {
  const first = displayName.trim().split(/\s+/)[0]
  return first && first.length > 1 ? first : displayName
}
